import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'assets/pdfjs/pdf.worker.min.js';

const MAX_PIXEL_RATIO = 2;
const RESIZE_DEBOUNCE_MS = 200;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

/**
 * Renders every page of a PDF Blob as a <canvas> stacked in a scrollable area.
 * Native <iframe> PDF viewers only show page 1 on iOS/Android, so pdf.js is used instead.
 * Emits `reachedEnd` once the user scrolls to the bottom of the last page.
 */
@Component({
  selector: 'app-pdf-page-viewer',
  templateUrl: './pdf-page-viewer.component.html',
  styleUrls: ['./pdf-page-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfPageViewerComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() blob: Blob | null = null;
  /** Shows a bar with page counter, zoom controls and download. */
  @Input() showToolbar = false;
  @Input() fileName = 'document.pdf';
  @Output() readonly rendered = new EventEmitter<void>();
  @Output() readonly reachedEnd = new EventEmitter<void>();
  @Output() readonly loadFailed = new EventEmitter<unknown>();

  @ViewChild('scroller', { static: true }) private scrollerRef!: ElementRef<HTMLElement>;
  @ViewChild('pages', { static: true }) private pagesRef!: ElementRef<HTMLElement>;
  @ViewChild('endSentinel', { static: true }) private sentinelRef!: ElementRef<HTMLElement>;

  private pdf: PDFDocumentProxy | null = null;
  private renderTasks: RenderTask[] = [];
  private renderId = 0;
  private endObserver: IntersectionObserver | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  private lastWidth = 0;
  private viewReady = false;
  private endEmitted = false;

  zoom = 1;
  currentPage = 1;
  totalPages = 0;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  get canZoomOut(): boolean {
    return this.zoom > MIN_ZOOM;
  }

  get canZoomIn(): boolean {
    return this.zoom < MAX_ZOOM;
  }

  get zoomLabel(): string {
    return `${Math.round(this.zoom * 100)}%`;
  }

  zoomIn(): void {
    this.setZoom(this.zoom + ZOOM_STEP);
  }

  zoomOut(): void {
    this.setZoom(this.zoom - ZOOM_STEP);
  }

  resetZoom(): void {
    this.setZoom(1);
  }

  download(): void {
    if (!this.blob) {
      return;
    }
    const url = URL.createObjectURL(this.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.fileName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  onScroll(): void {
    const scroller = this.scrollerRef.nativeElement;
    const canvases = Array.from(this.pagesRef.nativeElement.children) as HTMLElement[];
    const threshold = scroller.scrollTop + scroller.clientHeight / 3;
    let page = 1;
    canvases.forEach((canvas, index) => {
      if (canvas.offsetTop <= threshold) {
        page = index + 1;
      }
    });
    if (page !== this.currentPage) {
      this.currentPage = page;
      this.cdr.markForCheck();
    }
  }

  private setZoom(value: number): void {
    const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(value * 100) / 100));
    if (next === this.zoom || !this.pdf) {
      return;
    }
    this.zoom = next;
    void this.renderAllPages();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.observeResize();
    if (this.blob) {
      void this.load(this.blob);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['blob'] && this.viewReady && this.blob) {
      void this.load(this.blob);
    }
  }

  ngOnDestroy(): void {
    this.endObserver?.disconnect();
    this.resizeObserver?.disconnect();
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.cancelRendering();
    void this.pdf?.destroy();
  }

  private async load(blob: Blob): Promise<void> {
    try {
      this.endEmitted = false;
      this.currentPage = 1;
      this.endObserver?.disconnect();
      const data = new Uint8Array(await blob.arrayBuffer());
      await this.pdf?.destroy();
      this.pdf = await pdfjsLib.getDocument({ data }).promise;
      await this.renderAllPages();
    } catch (error) {
      this.loadFailed.emit(error);
    }
  }

  private async renderAllPages(): Promise<void> {
    const pdf = this.pdf;
    if (!pdf) {
      return;
    }

    const currentRender = ++this.renderId;
    this.cancelRendering();

    const container = this.pagesRef.nativeElement;
    const scroller = this.scrollerRef.nativeElement;
    const scrollRatio = scroller.scrollHeight > 0 ? scroller.scrollTop / scroller.scrollHeight : 0;
    this.lastWidth = scroller.clientWidth;
    const availableWidth = this.lastWidth * this.zoom;
    const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
    const fragment = document.createDocumentFragment();

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      const page = await pdf.getPage(pageNumber);
      if (currentRender !== this.renderId) {
        return;
      }

      const baseViewport = page.getViewport({ scale: 1 });
      const cssScale = availableWidth / baseViewport.width;
      const viewport = page.getViewport({ scale: cssScale * pixelRatio });

      const canvas = document.createElement('canvas');
      canvas.className = 'pdf-page';
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${Math.floor(viewport.width / pixelRatio)}px`;
      canvas.style.height = `${Math.floor(viewport.height / pixelRatio)}px`;
      fragment.appendChild(canvas);

      const context = canvas.getContext('2d');
      if (!context) {
        continue;
      }
      const task = page.render({ canvasContext: context, viewport });
      this.renderTasks.push(task);
      await task.promise;
      if (currentRender !== this.renderId) {
        return;
      }
    }

    container.replaceChildren(fragment);
    scroller.scrollTop = scrollRatio * scroller.scrollHeight;
    this.totalPages = pdf.numPages;
    this.onScroll();
    this.cdr.markForCheck();
    this.rendered.emit();
    this.observeEnd();
  }

  private observeEnd(): void {
    this.endObserver?.disconnect();
    this.endObserver = new IntersectionObserver(
      entries => {
        if (!this.endEmitted && entries.some(entry => entry.isIntersecting)) {
          this.endEmitted = true;
          this.reachedEnd.emit();
          this.endObserver?.disconnect();
        }
      },
      // Fractional scroll positions on high-DPR screens can leave the sentinel a hair short of fully visible.
      { root: this.scrollerRef.nativeElement, rootMargin: '0px 0px 4px 0px', threshold: 0 }
    );
    this.endObserver.observe(this.sentinelRef.nativeElement);
  }

  private observeResize(): void {
    this.resizeObserver = new ResizeObserver(() => {
      const width = this.scrollerRef.nativeElement.clientWidth;
      if (!this.pdf || width === this.lastWidth) {
        return;
      }
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => void this.renderAllPages(), RESIZE_DEBOUNCE_MS);
    });
    this.resizeObserver.observe(this.scrollerRef.nativeElement);
  }

  private cancelRendering(): void {
    this.renderTasks.forEach(task => task.cancel());
    this.renderTasks = [];
  }
}
